/**
 * V-NAV-DEVICE R1 — the ONE way into the vault. Every trigger calls this; none hardcodes a target.
 *
 * ⛔⛔ THE DEFECT. There were two vault UIs and half a dozen ways in, each deciding for itself:
 * MaestroNav's wordmark hardcoded `/maestro/vault/home` for every authed user, the account menu's
 * Vault item did the same, the mobile sheet's Vault link did the same, and storeApp's logo went to
 * an empty shop hub. So a DESKTOP user clicking the most prominent control in the app landed in the
 * PHONE vault on a 1440px display. Fixing the wordmark alone would have left the other four.
 *
 * ⭐ ONE FUNCTION, AND THE GREP IS THE PROOF. A trigger cannot get this wrong without visibly not
 * calling `openVault`. Searching src (excluding tests) for the literal route "maestro/vault/home"
 * or for "setIsVaultOpen" should find this module and App.jsx's route table, and nothing else.
 *
 * ⚠ THE DESKTOP VAULT IS STATE, NOT A ROUTE. It is App.jsx's `isVaultOpen` overlay. A caller
 * INSIDE App's tree can raise it directly and passes `openDesktopOverlay`; a caller in another
 * shell (MaestroNav renders on the homepage and the Supply Lab, outside storeApp) cannot touch
 * that state, so it navigates to "/" carrying a one-shot `?vault=open` flag that App consumes on
 * arrival. Same destination, two mechanisms, one decision point.
 */
import { isMobileDevice } from './deviceClass';

export const VAULT_HOME = '/maestro/vault/home';
export const DESKTOP_VAULT_SIGNAL = '/?vault=open';

/**
 * Send the user to the vault that suits their DEVICE.
 *
 * @param {object} [options]
 * @param {(to: string) => void} [options.navigate] react-router navigate, when the caller has one.
 *   Without it the module falls back to a full-page assign, which is what a plain anchor handler
 *   in another shell needs.
 * @param {() => void} [options.openDesktopOverlay] raise `isVaultOpen` directly. Only App.jsx's
 *   own tree can do this; everyone else omits it and gets the `?vault=open` signal instead.
 */
export function openVault({ navigate, openDesktopOverlay } = {}) {
  const go = (to) => {
    if (navigate) navigate(to);
    else window.location.assign(to);
  };

  if (isMobileDevice()) {
    go(VAULT_HOME);
    return;
  }
  if (openDesktopOverlay) {
    openDesktopOverlay();
    return;
  }
  go(DESKTOP_VAULT_SIGNAL);
}

/**
 * The href a vault LINK should carry, for the same decision.
 *
 * Anchors are kept as anchors (middle-click, open-in-new-tab, and the accessible "link" role all
 * depend on a real href); this gives them the device-correct target without letting them invent
 * one. The desktop answer is the `?vault=open` signal because an anchor cannot raise App's state.
 */
export function vaultHref() {
  return isMobileDevice() ? VAULT_HOME : DESKTOP_VAULT_SIGNAL;
}

export default openVault;
